import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as GiIcons from 'react-icons/gi';

const { FiEye, FiEyeOff, FiLightbulb, FiTrash2, FiArrowUp } = FiIcons;
const { GiMagicSwirl } = GiIcons;

const RACOAPTERETOField = ({ data, value, onChange, onNavigateToTop }) => {
  const [showExample, setShowExample] = useState(false);
  const [showSuggestion, setShowSuggestion] = useState(false);

  const handleSuggestImprovement = () => {
    if (!value.trim()) {
      setShowSuggestion(true);
      return;
    }

    // Sugerencias inteligentes basadas en el campo y el contenido actual
    const improvementSuggestions = {
      rol: {
        basic: [
          "Considera especificar la experiencia o credenciales del rol.",
          "Añade una especialización o área de experiencia al rol.",
          "Incluye el nivel de experiencia (senior, experto, etc.)."
        ],
        advanced: {
          marketing: "Eres un estratega de marketing digital con 10+ años de experiencia en campañas virales y análisis de datos para optimización de conversión.",
          tech: "Eres un arquitecto de software especializado en sistemas escalables y microservicios con experiencia en empresas Fortune 500.",
          content: "Eres un redactor SEO senior con experiencia en copywriting persuasivo y optimización para motores de búsqueda.",
          business: "Eres un consultor estratégico de negocios con experiencia en transformación digital y optimización de procesos empresariales."
        }
      },
      accion: {
        basic: [
          "Utiliza verbos de acción más específicos y medibles.",
          "Describe con más detalle el proceso o metodología a seguir.",
          "Especifica las herramientas o enfoques a utilizar."
        ],
        advanced: {
          create: "Desarrolla estratégicamente utilizando metodologías comprobadas y un enfoque basado en datos.",
          analyze: "Analiza exhaustivamente utilizando técnicas avanzadas de interpretación y visualización de datos.",
          improve: "Optimiza sistemáticamente mediante pruebas A/B y análisis iterativo de rendimiento.",
          plan: "Diseña un plan estratégico integral con hitos claros y métricas de seguimiento."
        }
      },
      contexto: {
        basic: [
          "Proporciona más detalles sobre el entorno o circunstancias.",
          "Menciona factores externos relevantes que influyen en la situación.",
          "Incluye información sobre el público o usuarios involucrados."
        ],
        advanced: {
          business: "En un mercado altamente competitivo donde la diferenciación de marca es crucial y los consumidores buscan autenticidad y propósito.",
          tech: "En un entorno tecnológico en rápida evolución donde la experiencia de usuario y la seguridad son prioritarias para la adopción.",
          education: "En un contexto educativo donde el aprendizaje personalizado y las competencias digitales son cada vez más demandadas."
        }
      },
      objetivo: {
        basic: [
          "Define metas SMART (Específicas, Medibles, Alcanzables, Relevantes, Temporales).",
          "Incluye métricas concretas para medir el éxito.",
          "Especifica el impacto esperado a corto y largo plazo."
        ],
        advanced: {
          growth: "Incrementar el engagement en un 40% y la tasa de conversión en un 25% en los próximos 3 meses.",
          quality: "Mejorar la satisfacción del usuario de 3.2 a 4.5/5 y reducir las tasas de abandono en un 30%.",
          innovation: "Desarrollar una solución disruptiva que reduzca los costos operativos en un 35% mientras mejora la experiencia del usuario."
        }
      },
      audiencia: {
        basic: [
          "Especifica características demográficas como ubicación o intereses.",
          "Incluye intereses, comportamientos o necesidades específicas.",
          "Describe el nivel de conocimiento o familiaridad con el tema."
        ],
        advanced: {
          gen_z: "El contenido está dirigido a personas pertenecientes a la Generación Z interesadas en sostenibilidad y experiencias auténticas.",
          professionals: "El contenido está enfocado en profesionales de nivel medio y alto con alto poder adquisitivo y poco tiempo disponible.",
          parents: "El contenido está orientado a padres y madres primerizos preocupados por el bienestar y desarrollo de sus hijos."
        }
      },
      plataforma: {
        basic: [
          "Especifica características técnicas o limitaciones de la plataforma.",
          "Menciona el formato de contenido más efectivo para ese canal.",
          "Incluye información sobre el algoritmo o sistema de distribución."
        ],
        advanced: {
          instagram: "Para publicar en Instagram, aprovechando el formato de Reels de 30 segundos y las tendencias actuales de la plataforma.",
          linkedin: "Para compartir en LinkedIn como artículos de formato largo con infografías integradas, optimizados para el algoritmo profesional.",
          tiktok: "Para difundir en TikTok utilizando formatos verticales cortos con música y efectos virales que favorecen la distribución orgánica."
        }
      },
      tarea: {
        basic: [
          "Describe con más detalle los pasos específicos a realizar.",
          "Incluye herramientas o metodologías para completar la tarea.",
          "Especifica los entregables o resultados intermedios esperados."
        ],
        advanced: {
          content: "Desarrolla un calendario editorial detallado con 12 publicaciones semanales, incluyendo copys, hashtags y horarios óptimos de publicación.",
          analysis: "Realiza un análisis comparativo exhaustivo de las 5 principales competencias, identificando fortalezas, debilidades y oportunidades de diferenciación.",
          strategy: "Diseña una estrategia integral de lanzamiento con fases definidas: pre-lanzamiento (2 semanas), lanzamiento (1 semana) y post-lanzamiento (4 semanas)."
        }
      },
      ejemplo: {
        basic: [
          "Proporciona un ejemplo más específico y detallado.",
          "Incluye métricas o resultados concretos en tu ejemplo.",
          "Relaciona el ejemplo claramente con el objetivo principal."
        ],
        advanced: {
          case_study: "Como ejemplo concreto, la campaña 'Eco-Future' de Patagonia que incrementó las ventas en un 35% mientras reducía su huella de carbono en un 25%.",
          demonstration: "Por ejemplo, la implementación de la metodología ágil en el equipo de desarrollo de Tesla que redujo el tiempo de entrega en un 40%.",
          comparison: "Como muestra, el rediseño de la experiencia de usuario de Spotify que aumentó el tiempo promedio de sesión de 18 a 32 minutos por usuario."
        }
      },
      resultado: {
        basic: [
          "Define métricas específicas para medir el éxito.",
          "Establece plazos claros para los resultados esperados.",
          "Incluye tanto resultados cuantitativos como cualitativos."
        ],
        advanced: {
          growth: "Logrando un incremento mínimo del 45% en conversiones y un aumento de LTV del 30% en el primer trimestre.",
          quality: "Alcanzando una tasa de satisfacción del cliente del 95% y reduciendo las solicitudes de soporte en un 40%.",
          awareness: "Consiguiendo un alcance orgánico de 2 millones de impresiones y un incremento del 65% en menciones de marca en redes sociales."
        }
      },
      estilo: {
        basic: [
          "Especifica aspectos visuales como paleta de colores o tipografía.",
          "Define la estructura o formato de presentación del contenido.",
          "Incluye referencias a estilos o tendencias específicas."
        ],
        advanced: {
          minimal: "Utilizando un estilo minimalista con amplio espacio en blanco, tipografía sans-serif y una paleta monocromática con acentos de color azul.",
          corporate: "Empleando un estilo corporativo profesional con gráficos vectoriales, datos visualizados con claridad y una estructura jerárquica consistente.",
          creative: "Aplicando un enfoque creativo con ilustraciones personalizadas, transiciones dinámicas y una combinación audaz de tipografías serif y sans-serif."
        }
      },
      tono: {
        basic: [
          "Especifica la actitud o emoción que debe transmitir el contenido.",
          "Define el nivel de formalidad o cercanía con la audiencia.",
          "Incluye ejemplos de expresiones o frases que reflejen el tono deseado."
        ],
        advanced: {
          professional: "Manteniendo un tono profesional pero accesible, con datos respaldados por fuentes confiables y un lenguaje preciso sin tecnicismos innecesarios.",
          conversational: "Utilizando un tono conversacional y cercano, como si hablaras con un amigo informado, incluyendo preguntas retóricas y lenguaje cotidiano.",
          inspirational: "Empleando un tono inspirador y motivacional que genere entusiasmo, con historias de superación y un lenguaje orientado a la acción."
        }
      },
      opciones: {
        basic: [
          "Proporciona alternativas más diversas y contrastadas.",
          "Explica brevemente los pros y contras de cada opción.",
          "Incluye opciones para diferentes contextos o recursos disponibles."
        ],
        advanced: {
          comprehensive: "Como alternativas, considera: 1) una versión simplificada enfocada en resultados rápidos con menor inversión, 2) un enfoque integral con mayor inversión pero resultados más sostenibles, o 3) una implementación por fases que equilibre inversión y resultados.",
          specialized: "Alternativamente, puedes optar por: 1) un enfoque especializado en SEO con contenido optimizado para buscadores, 2) una estrategia centrada en redes sociales con contenido viral, o 3) un modelo híbrido que combine ambos enfoques según la etapa del embudo.",
          budget: "Como opciones adicionales según presupuesto: 1) versión premium con análisis detallado y seguimiento continuo, 2) versión estándar con implementación básica y seguimiento mensual, o 3) versión esencial con guías de implementación y soporte limitado."
        }
      }
    };

    // Obtener sugerencias relevantes para el campo actual
    const fieldKey = data.key;
    const suggestions = improvementSuggestions[fieldKey];

    if (suggestions) {
      // Determinar el tipo de contenido y seleccionar sugerencia apropiada
      const currentText = value.toLowerCase();
      let suggestionText = "";

      // Seleccionar sugerencia básica aleatoria si no hay coincidencias específicas
      const basicSuggestions = suggestions.basic || [];
      suggestionText = basicSuggestions[Math.floor(Math.random() * basicSuggestions.length)] || "";

      // Intentar encontrar sugerencias avanzadas más específicas
      if (suggestions.advanced) {
        const contentType = determineContentType(currentText);
        if (contentType && suggestions.advanced[contentType]) {
          suggestionText = `Sugerencia: ${suggestions.advanced[contentType]}`;
        }
      }

      if (suggestionText) {
        alert(suggestionText);
      } else {
        alert("Considera añadir más detalles específicos para este campo.");
      }
    } else {
      // Sugerencia genérica para campos sin sugerencias específicas
      alert("Considera añadir más detalles específicos y contexto relevante para este campo.");
    }
  };

  // Determinar el tipo de contenido basado en palabras clave
  const determineContentType = (text) => {
    const contentTypes = {
      marketing: ['marketing', 'ventas', 'publicidad', 'marca', 'audiencia', 'social media', 'promoción'],
      tech: ['software', 'tecnología', 'código', 'sistema', 'aplicación', 'digital', 'web', 'app'],
      content: ['contenido', 'escribir', 'texto', 'artículo', 'blog', 'redacción', 'copywriting', 'calendario', 'publicaciones'],
      business: ['negocio', 'empresa', 'estrategia', 'gerente', 'director', 'ejecutivo', 'líder'],
      create: ['crear', 'desarrollar', 'diseñar', 'elaborar', 'construir', 'producir'],
      analyze: ['analizar', 'evaluar', 'investigar', 'examinar', 'estudiar', 'revisar', 'comparativo', 'análisis'],
      improve: ['mejorar', 'optimizar', 'aumentar', 'incrementar', 'potenciar', 'perfeccionar'],
      plan: ['planificar', 'organizar', 'estructurar', 'preparar', 'coordinar', 'estrategia', 'lanzamiento'],
      growth: ['crecimiento', 'aumentar', 'incrementar', 'expandir', 'escalar', 'conversiones'],
      quality: ['calidad', 'mejorar', 'satisfacción', 'experiencia', 'optimizar'],
      innovation: ['innovar', 'disruptivo', 'revolucionar', 'transformar', 'reinventar'],
      gen_z: ['generación z', 'jóvenes', 'adolescentes', 'centennials', 'tiktok', 'instagram'],
      professionals: ['profesionales', 'ejecutivos', 'corporativo', 'empresa', 'carrera', 'trabajo'],
      parents: ['padres', 'madres', 'hijos', 'familia', 'niños', 'crianza'],
      instagram: ['instagram', 'fotos', 'reels', 'stories', 'visuales', 'imágenes'],
      linkedin: ['linkedin', 'profesional', 'b2b', 'negocios', 'corporativo', 'empresarial'],
      tiktok: ['tiktok', 'videos cortos', 'tendencias', 'viral', 'música', 'baile'],
      case_study: ['caso', 'ejemplo', 'estudio', 'muestra', 'referencia', 'patagonia', 'nike', 'apple'],
      demonstration: ['demostración', 'prueba', 'implementación', 'tesla', 'método', 'metodología'],
      comparison: ['comparación', 'contraste', 'versus', 'spotify', 'rediseño', 'experiencia'],
      awareness: ['conocimiento', 'visibilidad', 'reconocimiento', 'alcance', 'impresiones', 'menciones'],
      minimal: ['minimalista', 'simple', 'limpio', 'espacio', 'blanco', 'monocromático'],
      corporate: ['corporativo', 'profesional', 'negocios', 'formal', 'jerárquico', 'vectorial'],
      creative: ['creativo', 'artístico', 'innovador', 'ilustraciones', 'dinámico', 'audaz'],
      professional: ['profesional', 'formal', 'serio', 'datos', 'preciso', 'confiable'],
      conversational: ['conversacional', 'cercano', 'amigable', 'informal', 'cotidiano', 'preguntas'],
      inspirational: ['inspirador', 'motivacional', 'entusiasmo', 'superación', 'acción', 'emocionante'],
      comprehensive: ['completo', 'integral', 'exhaustivo', 'detallado', 'profundo', 'abarcador'],
      specialized: ['especializado', 'específico', 'focalizado', 'concreto', 'particular', 'seo'],
      budget: ['presupuesto', 'costo', 'inversión', 'precio', 'económico', 'premium']
    };

    // Encontrar el tipo con más coincidencias
    let bestMatch = null;
    let maxMatches = 0;

    Object.entries(contentTypes).forEach(([type, keywords]) => {
      const matches = keywords.filter(keyword => text.includes(keyword)).length;
      if (matches > maxMatches) {
        maxMatches = matches;
        bestMatch = type;
      }
    });

    return maxMatches > 0 ? bestMatch : null;
  };

  const clearField = () => {
    onChange('');
  };

  // Función para obtener el placeholder específico para cada campo
  const getPlaceholder = () => {
    const placeholders = {
      rol: "Describe el rol...",
      accion: "Describe la acción...",
      contexto: "Describe el contexto...",
      objetivo: "Describe el objetivo...",
      audiencia: "Describe la audiencia...",
      plataforma: "Describe la plataforma...",
      tarea: "Describe la tarea...",
      ejemplo: "Describe el ejemplo...",
      resultado: "Describe el resultado...",
      estilo: "Describe el estilo...",
      tono: "Describe el tono...",
      opciones: "Describe las opciones..."
    };

    return placeholders[data.key] || `Describe el ${data.title.toLowerCase()}...`;
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-6 bg-gray-50 dark:bg-gray-900 hover:shadow-md transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-2">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-[#2B5596] text-white rounded-lg flex items-center justify-center font-bold text-xs sm:text-sm">
              {data.letter}
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
              {data.title}
            </h3>
            <motion.button
              onClick={onNavigateToTop}
              className="p-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label={`Ir a recuadros de ${data.title}`}
              title={`Ir a recuadros de ${data.title}`}
            >
              <SafeIcon icon={FiArrowUp} className="w-3 h-3 sm:w-4 sm:h-4" />
            </motion.button>
          </div>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3">
            {data.description}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <motion.button
            onClick={() => setShowExample(!showExample)}
            className="flex items-center space-x-1 text-xs sm:text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 sm:px-3 py-1 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={showExample ? FiEyeOff : FiEye} className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>{showExample ? 'Ocultar ejemplo' : '«Ver ejemplo»'}</span>
          </motion.button>

          <motion.button
            onClick={handleSuggestImprovement}
            className="flex items-center space-x-1 text-xs sm:text-sm bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 px-2 sm:px-3 py-1 rounded-lg hover:bg-yellow-200 dark:hover:bg-yellow-800 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={FiLightbulb} className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>«Sugerir mejoras»</span>
          </motion.button>

          {value && (
            <motion.button
              onClick={clearField}
              className="flex items-center space-x-1 text-xs sm:text-sm bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 px-2 sm:px-3 py-1 rounded-lg hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={FiTrash2} className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Limpiar campo</span>
            </motion.button>
          )}
        </div>

        <AnimatePresence>
          {showExample && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-3 sm:p-4 rounded-r-lg"
            >
              <div className="flex items-start space-x-2">
                <SafeIcon icon={GiMagicSwirl} className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs sm:text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">Ejemplo:</p>
                  <p className="text-xs sm:text-sm text-blue-700 dark:text-blue-400">{data.example}</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={getPlaceholder()}
          className="w-full p-3 sm:p-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 resize-none transition-all text-sm sm:text-base"
          rows={3}
        />
      </div>
    </div>
  );
};

export default RACOAPTERETOField;